import React, { useState } from 'react';
import {
  AreaChart as RechartLineChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  ReferenceLine,
  Dot,
  ReferenceArea,
} from 'recharts';
import * as Styled from './LineChart.styles';
import { useTheme } from 'styled-components';
import { formatDateTime } from '../../utils';
import { GraphsData } from '@hud/portal-dtos/webapp-common.dto';
import { getSingularDot, shouldShowAnimation } from '../../Charts/LineChart/SingularDot';
import { AxisDomain } from 'recharts/types/util/types';

const SPACING = {
  normal: {
    YAxis: { top: 26, bottom: 20 },
    Chart: { top: 0, right: 20, left: 20, bottom: 0 },
  },
  compact: {
    YAxis: { top: 12, bottom: 12 },
    Chart: { top: 0, right: 20, left: 20, bottom: 0 },
  },
};

const YAxisWidth = 50;

export type XReferenceLine = {
  xAxisValue: string | number | Date;
  type?: 'warning' | 'part-warning';
  [key: string]: unknown;
};

export type DataKey = {
  key: string;
  color?: string;
  label?: string;
  hidden?: boolean;
};

export type DataKeyDef = {
  yInterval?: number;
  domain?: AxisDomain;
  title: string | React.ReactNode;
  dataKeys: DataKey[];
  headerComponent?: React.ReactNode;
  labelFormatter?: (value: any, index: number) => string;
  tooltipFormatter?: (value: any) => string;
};

export type Range = {
  start: Date;
  end: Date;
};

export interface LineChartProps {
  graphId: string;
  className?: string;
  width: number;
  headerWidth: number;
  height: number;
  data: GraphsData;
  referenceLines?: XReferenceLine[];
  XdataKey: string;
  dataKeyDefs: DataKeyDef[];

  renderReference?: (reference: XReferenceLine) => React.ReactNode;
  labelFormatter?: (value: any, index: number) => string;
  interval?: number;

  hideAxis?: boolean;
  compact?: boolean;
  onRangeSelected?: (range: Range) => void;
}

export function LineChart({
  graphId,
  className,
  width,
  headerWidth,
  height,
  XdataKey,
  dataKeyDefs,
  referenceLines,
  data,
  labelFormatter,
  renderReference,
  interval,
  hideAxis = false,
  compact = false,
  onRangeSelected,
}: LineChartProps) {
  const [activeReference, setActiveReference] = React.useState<XReferenceLine>();

  return (
    <Styled.Container className={className}>
      {/* Show Sticky tooltip WITHOUT reference lines */}
      {compact && (
        <ReferenceXAxis
          graphId={graphId}
          width={width}
          headerWidth={headerWidth}
          height={28}
          data={data}
          XdataKey={XdataKey}
          referenceLines={referenceLines}
          renderReference={renderReference}
          onActiveReference={setActiveReference}
          stickyTooltip
          showReferenceTicks={false}
          showTooltip={true}
        />
      )}
      <ReferenceXAxis
        graphId={graphId}
        width={width}
        headerWidth={headerWidth}
        height={28}
        data={data}
        XdataKey={XdataKey}
        referenceLines={referenceLines}
        renderReference={renderReference}
        onActiveReference={setActiveReference}
        stickyTooltip={false}
        showReferenceTicks={referenceLines && referenceLines.length > 0}
        showTooltip={!compact}
      />
      {dataKeyDefs.map((dataKeyDef) => (
        <SingleLineChart
          // TODO: Eyal - improve key
          key={dataKeyDef.dataKeys[0].key}
          graphId={graphId}
          width={width}
          headerWidth={headerWidth}
          height={height}
          data={data}
          title={dataKeyDef.title}
          XdataKey={XdataKey}
          YdataKeys={dataKeyDef.dataKeys}
          headerComponent={dataKeyDef.headerComponent}
          referenceLines={referenceLines}
          activeReference={activeReference}
          labelFormatter={labelFormatter}
          yLabelFormatter={dataKeyDef.labelFormatter}
          tooltipFormatter={dataKeyDef.tooltipFormatter}
          interval={interval}
          yInterval={dataKeyDef.yInterval}
          domain={dataKeyDef.domain}
          withXAxis={false}
          withYAxis={!hideAxis}
          compact={compact}
          onRangeSelected={onRangeSelected}
        />
      ))}

      {!hideAxis && (
        <CustomXAxis
          graphId={graphId}
          width={width}
          headerWidth={headerWidth}
          height={32}
          data={data}
          XdataKey={XdataKey}
          labelFormatter={labelFormatter}
          interval={interval}
        />
      )}
    </Styled.Container>
  );
}

export interface SingleLineChartProps {
  graphId: string;

  width: number;
  headerWidth: number;
  height: number;

  XdataKey: string;
  YdataKeys: DataKey[];
  title: string | React.ReactNode;
  data: GraphsData;
  headerComponent?: React.ReactNode;

  interval?: number;
  yInterval?: number;
  domain?: AxisDomain;

  referenceLines?: XReferenceLine[];
  activeReference?: XReferenceLine;

  onRangeSelected?: (range: Range) => void;
  labelFormatter?: (value: any, index: number) => string;
  tooltipFormatter?: (value: any) => string;
  yLabelFormatter?: (value: any, index: number) => string;

  withXAxis?: boolean;
  withYAxis?: boolean;

  compact?: boolean;
}

const MIN_RANGE_ALLOWED = 60 * 60 * 1000; // 1 hour in milliseconds

export function SingleLineChart({
  graphId,
  width,
  headerWidth,
  height,
  data,
  title,
  XdataKey,
  YdataKeys,
  labelFormatter,
  yLabelFormatter,
  tooltipFormatter,
  headerComponent,
  interval,
  yInterval,
  domain,
  referenceLines,
  activeReference,
  withXAxis = false,
  withYAxis = true,
  compact = false,
  onRangeSelected,
  ...rest
}: SingleLineChartProps) {
  const theme = useTheme();

  const singularDot = getSingularDot(data);
  const [areaLeft, setAreaLeft] = useState<Date>();
  const [areaRight, setAreaRight] = useState<Date>();

  const resetSelectionArea = () => {
    setAreaLeft(undefined);
    setAreaRight(undefined);
  };
  const handleMouseUp = () => {
    if (!areaLeft || !areaRight || !onRangeSelected) {
      resetSelectionArea();
      return;
    }
    const [start, end] =
      areaLeft <= areaRight ? [areaLeft, areaRight] : [areaRight, areaLeft];

    // Ensure time difference is at least 1 hour
    const timeDiff = end.getTime() - start.getTime();
    if (timeDiff < MIN_RANGE_ALLOWED) {
      resetSelectionArea();
      return;
    }

    onRangeSelected({ start, end });
    resetSelectionArea();
  };

  return (
    <Styled.SingleChartContainer>
      <Styled.SingleChartHeader width={headerWidth}>
        <Styled.Title>{title}</Styled.Title>
        {headerComponent && <Styled.Title>{headerComponent}</Styled.Title>}
      </Styled.SingleChartHeader>
      <Styled.SingleChartBody>
        {data.length === 0 && <Styled.ChartSkeleton style={{ height: height }} />}
        {data.length > 0 && (
          <RechartLineChart
            syncId={graphId}
            width={width - headerWidth}
            height={height}
            data={data}
            margin={compact ? SPACING.compact.Chart : SPACING.normal.Chart}
            onMouseDown={(e) => {
              if (onRangeSelected && e.activeLabel) {
                setAreaLeft(new Date(e.activeLabel));
              }
            }}
            onMouseMove={(e) => {
              if (areaLeft && e.activeLabel) {
                setAreaRight(new Date(e.activeLabel));
              }
            }}
            onMouseUp={handleMouseUp}
            {...rest}
          >
            {/* <CartesianGrid strokeDasharray="3 3" /> */}
            <XAxis
              dataKey={XdataKey}
              tickLine={false}
              fontSize="12"
              stroke={theme.textTertiary}
              hide={!withXAxis}
              interval={interval}
              tickFormatter={labelFormatter}
            />

            <YAxis
              width={YAxisWidth}
              interval={yInterval}
              axisLine={false}
              tickLine={false}
              fontSize="12"
              stroke={theme.textTertiary}
              padding={compact ? SPACING.normal.YAxis : SPACING.normal.YAxis}
              tickFormatter={yLabelFormatter}
              hide={!withYAxis}
              domain={domain}
            />
            <Tooltip
              content={({ payload }) => {
                if (!payload || payload.length === 0) return <></>;
                return (
                  <Styled.TooltipContainer>
                    {payload.map((entry) => (
                      <Styled.TooltipEntry color={entry.color} key={entry.name}>
                        {payload.length > 1 && (
                          <Styled.Dot size={6} color={entry.color} />
                        )}
                        {tooltipFormatter ? tooltipFormatter(entry.value) : entry.value}
                      </Styled.TooltipEntry>
                    ))}
                  </Styled.TooltipContainer>
                );
              }}
              cursor={{
                stroke: theme.strokeLight,
                strokeWidth: 1,
                strokeLinecap: 'round',
              }}
            />
            {/* Data Keys */}
            {YdataKeys.map((keyName) => (
              <Area
                key={keyName.key}
                dataKey={keyName.key}
                stroke={keyName.color ?? theme.turquoiseSecondary}
                strokeWidth={1}
                fill={`url(#line-gradient)`}
                hide={keyName.hidden}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - props are transffered by Area
                animationDuration={compact ? 500 : undefined}
                isAnimationActive={shouldShowAnimation(data, keyName.key)}
                dot={singularDot as any}
                activeDot={(props) => {
                  return (
                    <Dot
                      {...props}
                      r={5}
                      stroke={keyName.color ?? theme.turquoiseSecondary}
                      strokeWidth={2}
                      fill={theme.positiveSecondary}
                    />
                  );
                }}
              />
            ))}

            {referenceLines &&
              referenceLines.map((reference) => {
                return (
                  <ReferenceLine
                    key={(reference.xAxisValue as Date).getTime()}
                    x={(reference.xAxisValue as Date).getTime()}
                    stroke={
                      activeReference?.xAxisValue === reference.xAxisValue
                        ? reference.type === 'warning' || reference.type === 'part-warning'
                          ? 'var(--red-200)'
                          : theme.purpleHome
                        : theme.transparent
                    }
                    strokeDasharray="2 2"
                  />
                );
              })}
            <defs>
              <linearGradient id="line-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={theme.turquoise} stopOpacity={0.15} />
                <stop offset="100%" stopColor={theme.turquoise} stopOpacity={0} />
              </linearGradient>
              <linearGradient
                id="line-compact-gradient"
                x1="195.111"
                y1="-701.148"
                x2="-24.3365"
                y2="-701.148"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0FFEE6" stopOpacity="0" />
                <stop offset="0.6" stopColor="#0FFEE6" />
                <stop offset="1" stopColor="#0FFEE6" stopOpacity="0" />
              </linearGradient>
            </defs>
            {areaLeft && areaRight && (
              <ReferenceArea
                x1={areaLeft?.getTime()}
                x2={areaRight?.getTime()}
                fill={theme.turquoise}
                fillOpacity={0.1}
                ifOverflow="visible"
              />
            )}
          </RechartLineChart>
        )}
      </Styled.SingleChartBody>
    </Styled.SingleChartContainer>
  );
}

export interface CustomXAxisProps {
  graphId: string;

  width: number;
  headerWidth: number;
  height: number;

  XdataKey: string;
  data: GraphsData;

  interval?: number;

  labelFormatter?: (value: any, index: number) => string;
}

export interface ReferenceXAxisProps
  extends Omit<CustomXAxisProps, 'interval' | 'labelFormatter'> {
  referenceLines?: XReferenceLine[];
  renderReference?: (reference: XReferenceLine) => React.ReactNode;
  onActiveReference?: (reference: XReferenceLine | undefined) => void;

  stickyTooltip?: boolean;
  showTooltip?: boolean;
  showReferenceTicks?: boolean;
}

function CustomXAxis({
  graphId,
  width,
  headerWidth,
  height,
  data,
  XdataKey,
  labelFormatter,
  interval,
  ...rest
}: CustomXAxisProps) {
  const theme = useTheme();
  return (
    <Styled.SingleChartContainer>
      <Styled.SingleChartHeader width={headerWidth}></Styled.SingleChartHeader>
      <Styled.SingleChartBody>
        <RechartLineChart
          syncId={graphId}
          width={width - headerWidth}
          height={height}
          data={data}
          margin={{
            ...SPACING.normal.Chart,
            left: SPACING.normal.Chart.left + YAxisWidth,
          }}
          {...rest}
        >
          <XAxis
            dataKey={XdataKey}
            axisLine={false}
            tickLine={false}
            fontSize="12"
            stroke={theme.textTertiary}
            interval={interval}
            tickFormatter={labelFormatter}
            minTickGap={18}
          />
        </RechartLineChart>
      </Styled.SingleChartBody>
    </Styled.SingleChartContainer>
  );
}

function ReferenceTick(props: any) {
  const {
    x,
    payload,
    tickFormatter,
    referenceLines,
    renderReference,
    onActiveReference,
  } = props;

  const data = tickFormatter ? tickFormatter(payload.value) : payload.value;

  if (data === '') {
    return <></>;
  }
  const v = (referenceLines as XReferenceLine[])?.find(
    (reference) => reference.xAxisValue === data,
  );
  if (!v) return <></>;

  return (
    <foreignObject
      x={x}
      y={0}
      width="5"
      height="5"
      style={{ overflow: 'visible' }}
      onMouseEnter={() => onActiveReference?.(v)}
      onMouseLeave={() => onActiveReference?.(undefined)}
    >
      {renderReference?.(v)}
    </foreignObject>
  );
}

function ReferenceXAxis({
  graphId,
  width,
  headerWidth,
  height,
  data,
  XdataKey,
  referenceLines,
  renderReference,
  onActiveReference,

  stickyTooltip = false,
  showTooltip = true,
  showReferenceTicks = true,
  ...rest
}: ReferenceXAxisProps) {
  const theme = useTheme();
  return (
    <Styled.ReferenceChartContainer sticky={stickyTooltip}>
      <Styled.SingleChartHeader width={headerWidth}></Styled.SingleChartHeader>
      <Styled.SingleChartBody>
        <RechartLineChart
          syncId={graphId}
          width={width - headerWidth}
          height={height}
          data={data}
          margin={{
            ...SPACING.normal.Chart,
            left: SPACING.normal.Chart.left + YAxisWidth,
          }}
          {...rest}
        >
          {showTooltip && (
            <Tooltip
              wrapperStyle={{ visibility: 'visible' }}
              content={({ active, label }) => {
                const d = new Date(label as number);
                const isValidDate = d instanceof Date && !isNaN(d.getTime());
                return (
                  <Styled.TooltipContainer
                    style={{
                      visibility: active && isValidDate ? 'visible' : 'hidden',
                    }}
                  >
                    {formatDateTime(d)}
                  </Styled.TooltipContainer>
                );
              }}
              cursor={{
                stroke: theme.strokeLight,
                strokeWidth: 1,
                strokeLinecap: 'round',
              }}
            />
          )}
          <XAxis
            dataKey={XdataKey}
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={
              showReferenceTicks ? (
                <ReferenceTick
                  data={data}
                  referenceLines={referenceLines}
                  onActiveReference={onActiveReference}
                  renderReference={renderReference}
                />
              ) : (
                false
              )
            }
          />
        </RechartLineChart>
      </Styled.SingleChartBody>
    </Styled.ReferenceChartContainer>
  );
}
